import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge } from "../../components/billing/BillingBadges";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { fetchBillingStats, isApiError } from "../../lib/billing-api";
import type { BillingStats } from "../../lib/billing-types";
import { formatAmount } from "../../lib/format";
import { useConfig } from "../../state/config-context";

function Breakdown({
  title,
  entries,
}: {
  title: string;
  entries: [string, number][];
}): ReactNode {
  return (
    <article className="card card--wide">
      <h2 className="card__title">{title}</h2>
      {entries.length === 0 ? (
        <p className="muted">—</p>
      ) : (
        <ul className="tag-list">
          {entries.map(([key, count]) => (
            <li className="tag" key={key}>
              <BillingStatusBadge status={key} />
              <span className="muted"> · {String(count)}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function BillingDashboardInner(): ReactNode {
  const { t, lang } = useConfig();
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setStats(await fetchBillingStats());
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const cards: { key: string; label: string; value: string }[] =
    stats === null
      ? []
      : [
          {
            key: "total",
            label: t("billing.dashboard.invoicesTotal"),
            value: formatAmount(stats.invoices_total_minor, locale),
          },
          {
            key: "paid",
            label: t("billing.dashboard.amountPaid"),
            value: formatAmount(stats.amount_paid_minor, locale),
          },
          {
            key: "due",
            label: t("billing.dashboard.balanceDue"),
            value: formatAmount(stats.balance_due_minor, locale),
          },
          {
            key: "overdue",
            label: t("billing.dashboard.overdue"),
            value: String(stats.overdue_count),
          },
          {
            key: "products",
            label: t("billing.dashboard.productsActive"),
            value: String(stats.products_active),
          },
          {
            key: "accounts",
            label: t("billing.dashboard.accountsActive"),
            value: String(stats.accounts_active),
          },
        ];

  return (
    <section className="page" aria-labelledby="billing-dashboard-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="billing-dashboard-title">{t("billing.dashboard.title")}</h1>
          <p className="muted">{t("billing.dashboard.subtitle")}</p>
        </div>
        <Link className="btn btn--ghost" to="/billing/invoices">
          {t("billing.dashboard.viewInvoices")}
        </Link>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && stats !== null ? (
        <>
          <div className="card-grid">
            {cards.map((card) => (
              <article className="card" key={card.key}>
                <h2 className="card__title">{card.label}</h2>
                <p className="card__value">{card.value}</p>
              </article>
            ))}
          </div>
          <p className="muted">{t("billing.dashboard.mixedCurrencyHint")}</p>
          <Breakdown
            title={t("billing.dashboard.byQuoteStatus")}
            entries={Object.entries(stats.quotes_by_status)}
          />
          <Breakdown
            title={t("billing.dashboard.byInvoiceStatus")}
            entries={Object.entries(stats.invoices_by_status)}
          />
        </>
      ) : null}
    </section>
  );
}

export function BillingDashboardPage(): ReactNode {
  return (
    <RequirePermission permission="billing.stats.read">
      <BillingDashboardInner />
    </RequirePermission>
  );
}
